import { useEffect, useRef, useState } from 'react'
import styles from './styles.module.css'
import { Maybe } from '@/types/codegen-contentful'

const cloudinaryUrl = 'https://images.angelstudios.com/image/upload'

const BASE_WIDTH = 300
const BASE_HEIGHT = 140

const ImageSlot = ({
  alt,
  src,
  maxWidth,
  height,
}: {
  alt: string
  src: string
  maxWidth?: Maybe<number>
  height: number
}) => (
  <div className="shrink-0 px-5">
    <img
      src={`${cloudinaryUrl}${src}`}
      alt={alt}
      className="object-contain"
      style={{
        height: `${height}px`,
        maxWidth: maxWidth ? `${maxWidth}px` : undefined,
      }}
    />
  </div>
)

/** Copies needed to fill viewport during scroll + 1 for seamless loop + 1 buffer */
const getCopyCount = (viewportWidth: number, copyWidth: number) => Math.max(2, Math.ceil(viewportWidth / copyWidth) + 2)

export const InfiniteScrollCarousel = ({
  images,
  speed = 8000,
}: {
  images: { alt: string; src: string; maxWidth?: Maybe<number> }[]
  speed?: number
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const copyRef = useRef<HTMLDivElement>(null)
  const [copyWidth, setCopyWidth] = useState<number | null>(null)
  const [copyCount, setCopyCount] = useState(4)

  const height =
    images.length > 0
      ? Math.round(BASE_HEIGHT * (Math.max(...images.map((img) => img.maxWidth ?? BASE_WIDTH)) / BASE_WIDTH))
      : BASE_HEIGHT

  useEffect(() => {
    const copyEl = copyRef.current
    const containerEl = containerRef.current
    if (!copyEl || !containerEl) return

    const update = () => {
      const width = copyEl.offsetWidth
      const viewport = containerEl.offsetWidth
      setCopyWidth(width)
      if (width > 0) setCopyCount(getCopyCount(viewport, width))
    }
    update()
    const observer = new ResizeObserver(update)
    observer.observe(containerEl)
    observer.observe(copyEl)
    return () => observer.disconnect()
  }, [images])

  return (
    <div
      ref={containerRef}
      className="relative w-full overflow-hidden"
      style={
        {
          height: `${height}px`,
          '--speed': `${speed}ms`,
          '--copy-width': copyWidth != null ? `${copyWidth}px` : '0px',
        } as React.CSSProperties
      }
    >
      <div className={styles.track}>
        {Array.from({ length: copyCount }, (_, i) => i + 1).map((copy) => (
          <div ref={copy === 1 ? copyRef : undefined} className={styles.copy} key={copy}>
            {images.map(({ alt, src, maxWidth }) => (
              <ImageSlot alt={alt} src={src} maxWidth={maxWidth} height={height} key={`${alt}-${copy}`} />
            ))}
          </div>
        ))}
      </div>
    </div>
  )
}
